import { SESSION_INACTIVITY_DETAIL } from "@/constants/session";
import { getClientLocale } from "@/i18n/locale";

import { recordApiActivity } from "./sessionActivity";
import { triggerSessionExpired } from "./sessionExpiry";

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_URL ?? "";
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  headers?: Headers;

  constructor(status: number, detail?: unknown, headers?: Headers) {
    super(`API error: ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.headers = headers;
  }
}

function parseContentDispositionFilename(disposition: string): string {
  // Prefer filename* (RFC 5987): charset'language'encoded-value
  const extMatch = disposition.match(/filename\*\s*=\s*[^']*'[^']*'([^;]+)/i);
  if (extMatch) {
    try {
      return decodeURIComponent(extMatch[1].trim());
    } catch {
      // fall through to filename=
    }
  }

  // Quoted filename="..." — use [^"]+ to avoid greedy overrun
  const quotedMatch = disposition.match(/filename="([^"]+)"/i);
  if (quotedMatch) return quotedMatch[1];

  // Unquoted token: filename=report.zip (stops at whitespace or ;)
  const tokenMatch = disposition.match(/filename=([^\s;]+)/i);
  if (tokenMatch) return tokenMatch[1];

  return "download";
}

async function parseErrorDetail(res: Response): Promise<unknown> {
  const contentType = res.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    try {
      const data = await res.json();
      if (typeof data === "object" && data !== null && "detail" in data) {
        return data.detail;
      }
      return data;
    } catch {
      return undefined;
    }
  }

  try {
    const text = await res.text();
    return text || undefined;
  } catch {
    return undefined;
  }
}

function buildRequestInit(options?: RequestInit) {
  const isFormData = options?.body instanceof FormData;

  return {
    ...options,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      "Accept-Language": getClientLocale(),
      ...options?.headers,
    },
    credentials: options?.credentials ?? "include",
  } satisfies RequestInit;
}

let refreshPromise: Promise<boolean> | null = null;
let refreshEnabled = true;
let refreshRejectedForInactivity = false;

export function setSessionRefreshEnabled(enabled: boolean) {
  refreshEnabled = enabled;
  if (enabled) refreshRejectedForInactivity = false;
}

export function tryRefreshSession(): Promise<boolean> {
  if (!refreshEnabled) return Promise.resolve(false);
  refreshPromise ??= fetch(getBaseUrl() + "/v1/auth/refresh", {
    method: "POST",
    credentials: "include",
  })
    .then(async (res) => {
      if (!res.ok && res.status >= 400 && res.status < 500) {
        refreshEnabled = false;
        if ((await parseErrorDetail(res)) === SESSION_INACTIVITY_DETAIL) {
          refreshRejectedForInactivity = true;
        }
      }
      return res.ok;
    })
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

export const isAuthPath = (path: string) => path.startsWith("/v1/auth/");

export function reportSessionExpired(detail: unknown) {
  const isInactivity = detail === SESSION_INACTIVITY_DETAIL || refreshRejectedForInactivity;
  triggerSessionExpired(isInactivity ? "inactivity" : "unauthenticated");
}

async function reportSessionExpiredFromResponse(res: Response) {
  reportSessionExpired(await parseErrorDetail(res.clone()));
}

async function fetchWithSessionRetry(path: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(getBaseUrl() + path, buildRequestInit(options));

  const isExpiredSession = res.status === 401 && !isAuthPath(path) && typeof window !== "undefined";
  if (!isExpiredSession) {
    if (res.ok) recordApiActivity(path);
    return res;
  }

  if (await tryRefreshSession()) {
    const retried = await fetch(getBaseUrl() + path, buildRequestInit(options));
    if (retried.ok) recordApiActivity(path);
    else if (retried.status === 401) await reportSessionExpiredFromResponse(retried);
    return retried;
  }

  await reportSessionExpiredFromResponse(res);
  return res;
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetchWithSessionRetry(path, options);

  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorDetail(res), res.headers);
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return res.json();
}

export async function apiFetchBlob(
  path: string,
  options?: RequestInit
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetchWithSessionRetry(path, options);

  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorDetail(res), res.headers);
  }

  const disposition = res.headers.get("content-disposition") ?? "";
  const filename = parseContentDispositionFilename(disposition);
  const blob = await res.blob();

  return { blob, filename };
}

export function resolveApiUrl(path?: string | null): string | undefined {
  if (!path) return undefined;

  try {
    return new URL(path, getBaseUrl() || window.location.origin).toString();
  } catch {
    return path;
  }
}

export function resolveWsUrl(path: string): string {
  const explicit = process.env.NEXT_PUBLIC_WS_API_URL;
  if (explicit) return explicit.replace(/\/$/, "") + path;
  const base = getBaseUrl() || (typeof window !== "undefined" ? window.location.origin : "");
  return base.replace(/^http/, "ws") + path;
}

/** Builds a URLSearchParams string from a plain object, omitting null/undefined values. Arrays are appended as repeated params. */
export function buildParams(
  params: Record<string, string | number | boolean | string[] | null | undefined>
): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      value.forEach((v) => sp.append(key, v));
    } else {
      sp.set(key, String(value));
    }
  }
  const str = sp.toString();
  return str ? `?${str}` : "";
}
